import type { NavView } from '../../lib/destinations.js';

/**
 * R24 — the narrow-viewport (≤860px) primary navigation. The side rail is hidden at
 * this width (CSS), so this fixed bottom tab bar carries the core destinations; the
 * full destination list (incl. the armed outreach hubs) stays reachable via the
 * "More" tab, which opens the ⌘K command palette (R13). Always rendered; CSS shows
 * it only at the narrow tier, so there is no JS/CSS disagreement.
 */
interface Tab {
  view: NavView;
  label: string;
  icon: React.ReactNode;
}

const TABS: ReadonlyArray<Tab> = [
  // B2 — Today is the default landing, so it leads the mobile primary nav too.
  { view: 'unified-today', label: 'Today', icon: <TodayIcon /> },
  { view: 'unified', label: 'Inbox', icon: <InboxIcon /> },
  { view: 'unified-followups', label: 'Follow-ups', icon: <ClockIcon /> },
  { view: 'unified-contacts', label: 'Contacts', icon: <PersonIcon /> },
  { view: 'unified-settings', label: 'Setup', icon: <GearIcon /> },
];

export function MobileTabBar({
  currentView,
  onChangeView,
  onOpenMore,
}: {
  currentView: NavView;
  onChangeView: (next: NavView) => void;
  onOpenMore: () => void;
}) {
  return (
    <nav className="mobile-tabbar" aria-label="Primary" data-testid="mobile-tabbar">
      {TABS.map((t) => {
        const active = currentView === t.view;
        return (
          <button
            key={t.view}
            type="button"
            className={`mobile-tab${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
            data-testid={`mobile-tab-${t.view}`}
            onClick={() => onChangeView(t.view)}
          >
            <span className="mobile-tab-icon" aria-hidden>
              {t.icon}
            </span>
            <span className="mobile-tab-label">{t.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        className="mobile-tab"
        data-testid="mobile-tab-more"
        onClick={onOpenMore}
      >
        <span className="mobile-tab-icon" aria-hidden>
          <MoreIcon />
        </span>
        <span className="mobile-tab-label">More</span>
      </button>
    </nav>
  );
}

/* ---- tiny line icons (currentColor, 20px) ---- */
function svg(children: React.ReactNode) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
function TodayIcon() {
  return svg(
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>,
  );
}
function InboxIcon() {
  return svg(
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>,
  );
}
function ClockIcon() {
  return svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </>,
  );
}
function PersonIcon() {
  return svg(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </>,
  );
}
function GearIcon() {
  return svg(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>,
  );
}
function MoreIcon() {
  return svg(
    <>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </>,
  );
}
